import { Injectable } from '@angular/core';
import { Process } from '../models/process';
import { Subject } from '../models/subject';
import { Observable, forkJoin, map, of, switchMap, take } from 'rxjs';
import { EventService } from './event.service';
import { ProcessService } from './process.service';
import { SubjectService } from './subject.service';
import { Status } from '../models/status';
import { MatDialog } from '@angular/material/dialog';
import { ProcessDialogComponent } from 'app/main/content/processes/process-dialog/process-dialog.component';
import { StatusService } from './status.service';
import { StateService } from './state.service';
import { ProcessType } from '../models/process-type';
import { ProcessTypeService } from './process-type.service';

@Injectable({
  providedIn: 'root'
})
export class CheckListService {

  constructor(
    private eventService: EventService,
    private processService: ProcessService,
    private subjectService: SubjectService,
    private processTypeService: ProcessTypeService,
    private statusService: StatusService,
    private stateService: StateService,
    public dialog: MatDialog,
  ) { }

  public isCheckListComplete(checkList: Process): boolean {
    var [incomplete, total] = this.getNumberOfIncompleteFieldsInCheckList(checkList);
    return (incomplete == 0);
  }

  public getNumberOfIncompleteFieldsInCheckList(checkList: Process): [incomplete: number, total: number] {
    // Get all fields except Summary and LinkedSubject for this checklist
    var allCheckListFields = checkList.processType.dataFields.filter(i => i.name != 'Summary' && i.name != 'LinkedSubject');

    // Get data from Checklist it list.
    var completedCheckListFields = checkList.getMetadata(checkList.processType);

    // Determine which fields are incomplete
    var iCnt = 0;
    allCheckListFields.forEach(i => {
      var j = completedCheckListFields.find(j => j.id === i.id);

      // If datafield not found in checklist, or found but no value, increment count of incomplete fields
      if (!j || (j && j.value == undefined)) iCnt++;
      else {
        // Check value is not blank
        try {
          var s: string = String(j.value);
          if (s == undefined || s == null || s.trim() == '' || s.length == 0)
            iCnt++;
        }
        catch {
          console.log("Failed to check value for " + i.name);
          iCnt++;
        }
      }
    });
    return [iCnt, allCheckListFields.length];
  }

  performChecklistProcessTypeCheck(
    changedSubject: Subject
  ): Observable<ChecklistResultItem> {
    // 1. Use SubjectService to perform checklist processType checks and return a list of process records that need to be completed.
    // 2. Open a checklist dialog for each process record and get user to complete them
    // 3. When all checklist dialogs are closed, return the list of process records that were completed
    return this.performChecklistProcessTypeCheckInternal(changedSubject)
      .pipe(
        take(1),        
        switchMap(
          async ([checklistProcess, caseClosedStatusRecord, reopenedCase]: [Process[], Status, boolean]) => {
            // We have a list of process records that need to be completed in checklistProcess.

            var result: ChecklistResultItem = new ChecklistResultItem();
            result.caseClosedStatusRecord = caseClosedStatusRecord;
            result.cancelCount = 0;
            result.reopenedCase=reopenedCase;

            // if no checklistProcess records then just return ok to save
            if (checklistProcess.length == 0) return result;

            // Loop over required process types and save process records.
            for (var i = 0; checklistProcess && i < checklistProcess.length; i++) {
              const dialogRef = this.dialog.open(ProcessDialogComponent, {
                disableClose: true
              });
              dialogRef.componentInstance.data = {
                processRec: checklistProcess[i],
                saveRecord: false,
              };
              var dlg = await dialogRef.afterClosed().toPromise();
              if (dlg && dlg.dlgCancelled === true) {
                result.cancelCount = result.cancelCount + 1;
                break;
              }
              else {
                var processRec = dlg.process;
                processRec.processType = dlg.processType;
                result.processList.push(processRec);
              }
            }
            return result;
          }
        )
      );
  }

  public SaveChecklistProcessRecords(processList: Process[], s: Subject) {
    if (!processList || processList.length < 1) return;

    var sub = [];
    processList?.forEach((i) => {
      var p = new Process(i);
      p.subjectId = s.id;
      p.subject = undefined;
      p.processType = null;
      p.dateTimeCompleted = new Date();

      // this.processService.onAfterSave.pipe(take(1)).subscribe((_) => {
      //   // Fire update notification when process records have been saved. This is used by checklist widget to refresh the list.
      //   console.log("Creating Checklist widget Signal")
      //   this.eventService.ChecklistRecordAdded(this.subject);
      // });
      sub.push(this.processService.saveAsObservable(p, i.processType.name + ' Checklist saved'));
    });

    forkJoin(sub).pipe(take(1)).subscribe((response) => {
      console.log("Creating Checklist widget Signal")
      // this.eventService.ChecklistRecordAdded(this.subject);
      this.eventService.ChecklistRecordAdded(s);
    });
  }


  /// Check a subject to see if any checklists are required.
  performChecklistProcessTypeCheckInternal(
    changedSubject: Subject
  ): Observable<[Process[], Status, boolean]> {
    // Only do this for Cases.
    if (changedSubject.subjectType.name.toLocaleLowerCase() != 'case')
      return of([[], null, false]);

    // Find closed State record
    var closedState = this.stateService.cache.find(
      (i) => i.name.toLocaleLowerCase() == 'closed' && i.hideRecord !== true
    );

    // Get ProcessTypes and original subject data to detect changes
    const fetch$ = [
      this.processTypeService.getAllData().pipe(take(1)),
      this.processService.getSubjectCheckLists(changedSubject).pipe(take(1)),
      this.subjectService.get(changedSubject.uid).pipe(take(1)),
      // this.processService.getSubjectProcesses(changedSubject).pipe(take(1)),
    ];
    return forkJoin(fetch$).pipe(
      map(([processTypes, processRecords, originalSubject]: [ProcessType[], Process[], Subject]) => {

        let checkListProcessRecords: Process[] = [];
        let checklistProcessTypes = processTypes.filter(
          (i) => i.hideRecord === false && i.isCheckList == true
        );    

        let oSubject = new Subject(originalSubject);
        oSubject.subjectTypeId = changedSubject.subjectTypeId;
        oSubject.subjectType = changedSubject.subjectType;
        let originalDateClosed = oSubject.getDataFieldValue('dateclosed', true);
        let originalCategory = oSubject.categoryId;
        let originalStatusId = oSubject.getDataFieldValue('status', true);
        let originalType = oSubject.getDataFieldValue('type', true);
        let originalStatus = this.getStatusIdFromString(originalStatusId);
        let originalState = this.stateService.cache.find(
          (i) => i.id == originalStatus
        )?.id;

        let nSubject = new Subject(changedSubject);
        let newDateClosed = nSubject.getDataFieldValue('dateclosed', true);
        let newCategory = nSubject.categoryId;
        let newStatus = nSubject.getDataFieldValue('status', true);
        let newType = nSubject.getDataFieldValue('type', true);
        let newStatusId = -1;

        if (typeof newStatus !== "string")
          newStatus = String(newStatus);
        if (newStatus && newStatus.length > 0)
          newStatusId = this.getStatusIdFromString(newStatus);
        let newStatusRecord = this.statusService.cache.find(
          (i) => i.id == newStatusId
        );
        let newState = -1;
        if (newStatusRecord) newState = newStatusRecord.stateId;
        let caseClosedStatus: Status = null;

        // Check for reopened case
        var reopenedCase = (originalState == 1 && newState > 1);

        //Some users might not add a newDate yet when setting the case closed status, i recommend removing the condition for checking for a new date since it errors out the condition
        if (
          originalState != closedState.id &&
          newState == closedState.id &&
          newDateClosed
        ) {
          caseClosedStatus = newStatusRecord;
        }

        // If no originalStatusId then set it to -999
        if (!originalStatusId)
          originalStatusId = -999;

        var hasCaseChanged = false;
        if ((!originalCategory && newCategory) || (originalCategory && newCategory && originalCategory != newCategory)) hasCaseChanged = true
        if ((!originalType && newCategory) || (originalType && newCategory && originalType != newType)) hasCaseChanged = true
        if ((!originalStatus && newStatusId) || (originalStatus && newStatusId && originalStatus != newStatusId)) hasCaseChanged = true

        // If no change in Case Category or Type or Status then return empty array
        if (hasCaseChanged == false)
          return [checkListProcessRecords, caseClosedStatus, reopenedCase];

        if (reopenedCase == true) {          
          // Check for Reopen Checklist
          var reopenCheckpoint = checklistProcessTypes.filter(i => i.isCaseReopenedChecklist == true);
          if (reopenCheckpoint && reopenCheckpoint.length > 0) {            
            let process: Process = new Process();
            process.processTypeId = reopenCheckpoint[0]?.id;
            process.subjectId = changedSubject.id;
            checkListProcessRecords.push(process);
            return [checkListProcessRecords, caseClosedStatus, reopenedCase];
          }
        }

        // Check for all other Checklists (Not Reopen Checklists)
        checklistProcessTypes.filter(i=>i.isCaseReopenedChecklist==false).forEach((processType) => {
          let bAddCheckListProcess = false;
          if (
            // Check CaseCategory with CheckList Process Type or Case Category not selected
            ((processType.caseCategoryId != -1 && processType.caseCategoryId == newCategory)
              || processType.caseCategoryId == -1)

            // Check CaseType with CheckList Process Type or caseType not selected
            && ((processType.caseTypeId != -1 && processType.caseTypeId == newType)
              || processType.caseTypeId == -1)

            // Check for change in status and status matches CheckList Process Type settings or Case Status not selected
            && ((processType.caseStatusId != -1 && processType.caseStatusId == newStatusId)
              || processType.caseStatusId == -1)
          )
            // Then Add CheckList
            bAddCheckListProcess = true;

          // If we need to ask for a checklist, create a process record
          if (bAddCheckListProcess == true) {
            // Check to see if Checklist already exists, and only create new if it does not exist.
            var existingCheckListTypeFound: Process = processRecords.find(i => i.processTypeId == processType.id && i.subjectId == changedSubject.id);
            if (!existingCheckListTypeFound) {
              // Create new checklist to list to display
              let process: Process = new Process();
              process.processTypeId = processType.id;
              process.subjectId = changedSubject.id;
              checkListProcessRecords.push(process);
            }
            else {
              // Only prompt for checklist if it is not complete. We need to get checklist with process data and data fields first to complete this check.
              if (!this.isCheckListComplete(existingCheckListTypeFound)) {
                // Add existing checklist to list to display
                checkListProcessRecords.push(existingCheckListTypeFound);
              }
            }
          }
        });
        return [checkListProcessRecords, caseClosedStatus, reopenedCase];
      })
    );
  }

  getStatusIdFromString(str: string): number {
    if (!str) return -1;
    const match = String(str).match(/\d+$/);
    const number = match ? parseInt(match[0]) : NaN;
    return number;
  }

  getSecondaryText(checklist: Process): string {
    var [incomplete, total] = this.getNumberOfIncompleteFieldsInCheckList(checklist);
    if (incomplete > 0) {
      return "This checklist was started on " + this.formatDateTime(checklist.dateTimeCompleted) + ", " + incomplete + " out of " + total + ' fields are not yet completed';
    }
    else {
      return "This Checklist was started on " + this.formatDateTime(checklist.dateTimeCompleted) + " and has been completed";
    }
  }

  formatDateTime(dateToFormat: Date) {
    const date = new Date(dateToFormat);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const formattedDateAndTime: string = `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    return formattedDateAndTime;
  }

  // return the number of process types that are not hidden, are checklist and are caseReopenedChecklist and not the processType passed in
  getReopenChecklistCount(processType: ProcessType | null): number {
    return this.processTypeService.getCache().filter(i => i.isCaseReopenedChecklist == true && i.isCheckList == true && i.hideRecord == false && i.uid != processType?.uid).length;
  }
}

// Class use to return results
export class ChecklistResultItem {
  processList: Process[] = [];
  cancelCount: number = 0;
  caseClosedStatusRecord: Status = null;
  reopenedCase:boolean = false;
}
